import { autoinject } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { AccountService } from 'app/core/services/account-service';
import { Toaster } from 'app/shared/lib/toaster';
import { I18N } from 'aurelia-i18n';
import { EventAggregator } from 'aurelia-event-aggregator';
import * as Core from 'app/core/models/essette-api';
import { NotificationPreferenceReloadEvent } from 'app/core/lib/events';

interface CustomUserNotificationType extends Core.UserNotificationType {
    optIn: boolean;
}

interface NotificationTypeSections {
    sectionHeader: string;
    targetType: string;
    notificationTypes: CustomUserNotificationType[];
}

@autoinject()
export class Preferences extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected userId: string | null = null;

    protected notificationTypes: Core.UserNotificationType[] = [];
    protected sections: NotificationTypeSections[] = [];

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        protected service: AccountService,
        protected i18n: I18N,
        protected readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);

        // establish sections
        this.sections = [
            { sectionHeader: this.i18n.tr('core:local.myNotifications.correspondence'), targetType: 'Core.CorrespondenceLetter', notificationTypes: [] },
            { sectionHeader: this.i18n.tr('core:local.myNotifications.agCases'), targetType: 'AG.Case', notificationTypes: [] },
            { sectionHeader: this.i18n.tr('core:local.myNotifications.cmCases'), targetType: 'CM.Case', notificationTypes: [] },
            { sectionHeader: this.i18n.tr('core:local.myNotifications.csMemberIssues'), targetType: 'CS.MemberIssue', notificationTypes: [] },
            { sectionHeader: this.i18n.tr('core:local.myNotifications.csProviderIssues'), targetType: 'CS.ProviderIssue', notificationTypes: [] },
            { sectionHeader: this.i18n.tr('core:local.myNotifications.pmCandidates'), targetType: 'PM.Candidate', notificationTypes: [] },
            { sectionHeader: this.i18n.tr('core:local.myNotifications.umAuthorizations'), targetType: 'UM.Auth', notificationTypes: [] }
        ];
    }

    protected bind(): void {
        if(!this.userId) this.userId = this.env.user.id;
        this.reload();
    }

    ////****************** Events ***************************************************////


    ////****************** Functions ************************************************////
    protected async reload(): Promise<void> {
        await this.loadAccount();
    }

    protected organizeNotificationTypesByTargetType(): void {
        this.notificationTypes.forEach((notificationType) => {
            let seekTargetType = notificationType.targetType?.code;
            // consolidate household case notifications types with case notification types
            if(seekTargetType === 'CM.HouseholdCase') seekTargetType = 'CM.Case';
            const foundSection = this.sections.find(section => seekTargetType === section.targetType);
            // flip optOut to custom optIn property for <ess-checkbox>
            if(foundSection) foundSection.notificationTypes.push({
                ...notificationType,
                optIn: !notificationType.optOut || false
            });
        });
        // exclude sections that do not have notification types
        this.sections = this.sections.filter(section => !!section.notificationTypes.length);
    }

    protected buildNotificationPreferenceOptOuts(): Core.UserNotificationType[] {
        const notificationPreferences: Core.UserNotificationType[] = [];
        this.sections.forEach((section) => {
            // add to body only those notification types the user has un-checked/opted out of
            section.notificationTypes.forEach((notificationType) => {
                if(!notificationType.optIn) notificationPreferences.push({ id: notificationType.id, optOut: true });
            });
        });
        return notificationPreferences;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadAccount(): Promise<void> {
        if(!this.userId) return;

        const includes: string[] = ['notificationTypes', 'notificationTypes.application'];

        try {
            this.state.beginLoad();
            const response = await this.service.loadAccount(this.userId, includes);
            if(response.results[0]?.notificationTypes?.length) {
                this.notificationTypes = response.results[0].notificationTypes;
                this.organizeNotificationTypesByTargetType();
            }
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async saveNotificationPreferences(): Promise<void> {
        if(!this.userId) return;

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.updateNotificationPreferences(this.userId, this.buildNotificationPreferenceOptOuts());
            this.events.publish(new NotificationPreferenceReloadEvent());
            isSuccessful = response.isSuccessful;
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.updated('core:local.myNotifications.preference.your', isSuccessful, 2);
            this.state.endSave();
        }
    }
}
